import * as React from 'react';

// @ts-expect-error
import type {FieldArrayProps, FieldProps} from 'redux-form';

import {Session} from 'nutshell-core/session';

import {Button} from 'shells/button/button';
import {Label} from 'shells/form/label';
import {FormattedNumberField} from 'shells/formatted-number-field/formatted-number-field';
import {OptionCardBuilderField} from 'shells/redux-form-fields/option-card/option-card-builder-field';
import {SelectPickerGeneric} from 'shells/select-picker-generic/select-picker-generic';

import type {GoalField as GoalFieldFragment} from 'nutshell-graphql-types';

import {formatCurrencyValue} from '../../../../util/format-currency-value';

import {CreateLeadCustomFieldSidebar} from './create-lead-custom-field-sidebar';

import styles from './field-lead-fields.module.css';

type Props = {
    fields: FieldArrayProps['fields'];
    meta: FieldArrayProps['meta'];
    goalableFields: GoalFieldFragment[];
    refetchLeadFields: () => Promise<any>;
};

type State = {
    field: GoalFieldFragment | null | undefined;
    isCreatingField: boolean;
    leadFieldSelectText: string;
    currencyValue: number | null | undefined;
    // We need to keep track of which form input triggered the "Create new
    // custom field" option, so we'll store that in state.
    formInputCreatingCustomField:
        | {
              input: FieldProps['input'];
          }
        | null
        | undefined;
};

const NEW_CUSTOM_FIELD_OPTION_NAME = 'newCustomField';

export class FieldLeadFieldsComponent extends React.PureComponent<Props, State> {
    constructor() {
        // @ts-expect-error
        super();
        this.state = {
            field: null,
            isCreatingField: false,
            leadFieldSelectText: '',
            formInputCreatingCustomField: null,
            currencyValue: 0,
        };
    }

    render() {
        if (this.state.isCreatingField) {
            return (
                <CreateLeadCustomFieldSidebar
                    customFieldName={this.state.leadFieldSelectText}
                    onCreateSuccess={(newCustomField?: GoalFieldFragment | null) => {
                        // Assuming we successfully get back a new custom field,
                        // lets call onFieldSelect immediately, so that the user
                        // doesn't need to re-select the field they just created
                        if (this.state.formInputCreatingCustomField && newCustomField) {
                            this.onFieldSelect(this.state.formInputCreatingCustomField)({
                                value: newCustomField,
                            });
                        }

                        // Clear our inputs
                        this.setState({
                            isCreatingField: false,
                            leadFieldSelectText: '',
                            formInputCreatingCustomField: null,
                        });

                        // Refetch our fields, just cuz
                        this.props.refetchLeadFields();
                    }}
                    onClose={() =>
                        this.setState({
                            isCreatingField: false,
                            leadFieldSelectText: '',
                            formInputCreatingCustomField: null,
                        })
                    }
                />
            );
        }

        return (
            <div className={styles['card-container']}>
                <OptionCardBuilderField
                    fields={this.props.fields}
                    divider='And'
                    meta={this.props.meta}
                    getCardData={this.getCardData}
                    cardCreator={this.cardCreator}
                />
            </div>
        );
    }

    /**
     * This gets a list of all fields avaiable to learn about the lead. This
     * includes all custom fields, and any extra fields we add, eg. the expected
     * lead close date.
     *
     * @return {[Array]} The list of fields avaiable.
     */
    getAllFields = () => {
        const createCustomFieldOption = {
            __typename: 'FieldString',
            name: NEW_CUSTOM_FIELD_OPTION_NAME,
            filterTitle: '+ Create new custom field',
            type: 'STRING',
        } as const;

        const goalableFields = this.props.goalableFields;

        // @ts-expect-error
        return [createCustomFieldOption].concat(goalableFields);
    };

    getCardData = (selected: any) => {
        const selectedField = this.getAllFields().find(
            (field) => field.name === selected.fieldName
        );
        const topRowText = selectedField ? selectedField.filterTitle : selected.fieldName;

        return {
            topRow: topRowText,
            bottomRow: this.getCardBottomText(selected),
        };
    };

    getCardBottomText = (selected: any) => {
        if (selected.isSetValue) {
            return 'is collected';
        } else if (selected.fieldType === 'ENUM') {
            return `is ${selected.enumValue}`;
        } else if (selected.fieldType === 'CURRENCY') {
            return `is greater than ${formatCurrencyValue(
                selected.currencyValue,
                Session.getInstanceDefaultCurrencyCode()
            )}`;
        }

        return 'is collected';
    };

    cardCreator = (cardProps: {
        meta: FieldProps['meta'];
        input: FieldProps['input'];
        onRemove: () => any;
    }) => {
        const availableFields = getUnusedFields(this.getAllFields(), this.props.fields);
        const options = availableFields.map((field) => ({value: field, label: field.filterTitle}));

        return (
            <div className={styles['lead-field-field']}>
                <SelectPickerGeneric
                    // required={true}
                    autoFocus={true}
                    value={options.filter(
                        ({value}) => this.state.field && value.name === this.state.field.name
                    )}
                    onInputChange={this.handleInputChange}
                    filterOption={this.filterSelectOption}
                    options={options}
                    placeholder='Choose a field…'
                    onChange={this.onFieldSelect({input: cardProps.input})}
                />
                {this.state.field && this.state.field.__typename === 'FieldEnum' ? (
                    <div className={styles['enum-option']}>
                        <Label htmlFor='lead-field-enum-option'>Is:</Label>
                        <SelectPickerGeneric
                            id='lead-field-enum-option'
                            // required={true}
                            autoFocus={true}
                            options={[{value: '*', label: '-- Any value --'}].concat(
                                this.state.field.options.map((option) => ({
                                    value: option,
                                    label: option,
                                }))
                            )}
                            placeholder='Choose a value…'
                            onChange={this.onOptionSelect({input: cardProps.input})}
                        />
                    </div>
                ) : null}
                {this.state.field && this.state.field.type === 'CURRENCY' ? (
                    <div>
                        <Label htmlFor='currencyValue'>is greater than…</Label>
                        <FormattedNumberField
                            onChange={(number) => {
                                this.setState({currencyValue: number});
                            }}
                            onEnter={this.onCurrencyEnter({input: cardProps.input})}
                            value={this.state.currencyValue}
                        />
                    </div>
                ) : null}
                <div className={styles['field-actions']}>
                    {this.state.field && this.state.field.type === 'CURRENCY' ? (
                        <Button onClick={this.onCurrencyEnter({input: cardProps.input})}>OK</Button>
                    ) : null}
                    {this.props.fields.length > 1 ? (
                        <Button onClick={cardProps.onRemove}>Cancel</Button>
                    ) : undefined}
                </div>
            </div>
        );
    };

    filterSelectOption = (
        option: {
            label: string;
            value: GoalFieldFragment;
        },
        filterText: string
    ) => {
        if (option.value.name === NEW_CUSTOM_FIELD_OPTION_NAME) {
            return true;
        }

        return option.value.filterTitle.includes(filterText);
    };

    handleInputChange = (
        newVal: string,
        {
            action,
        }: {
            action: 'set-value' | 'menu-close' | 'input-change' | 'input-blur';
        }
    ) => {
        // Only update input value when user changes input value, this way correct
        // name is passed to create custom field component.
        if (action === 'input-change') {
            this.setState({
                leadFieldSelectText: newVal,
            });
        }
    };

    // We need this first param `field` so that we can store in state the
    // field that triggered the "Create new custom field" click. We'll
    // store that so we can update that field's selection with the new custom
    // field upon successfully creation.
    handleCreateNewCustomFieldClick = (field: {input: FieldProps['input']}) => () => {
        this.setState({
            isCreatingField: true,
            formInputCreatingCustomField: field,
        });
    };

    onFieldSelect =
        (field: {input: FieldProps['input']}) => (selected: {value: GoalFieldFragment}) => {
            const leadField = selected.value;

            if (leadField.name === NEW_CUSTOM_FIELD_OPTION_NAME) {
                this.handleCreateNewCustomFieldClick(field)();

                return;
            }

            const requiresInput =
                leadField.type === 'ENUM' ||
                (leadField.type === 'CURRENCY' && leadField.name === 'value');

            if (requiresInput) {
                // apollo-codegen isn't generating a real disjoint union type
                // here so we are doing a cast to convince flow
                const enumField: GoalFieldFragment = leadField as any;

                this.setState({field: enumField});
            } else {
                field.input.onChange({
                    fieldName: leadField.name,
                    isSetValue: true,
                });

                // Make certain we clear any enum field in temp holding
                this.setState({field: null});
            }
        };

    onOptionSelect = (field: {input: FieldProps['input']}) => (selected: {value: string}) => {
        const leadField = this.state.field;
        if (!leadField) return;

        const condition =
            selected.value === '*'
                ? {
                      fieldName: leadField.name,
                      isSetValue: true,
                  }
                : {
                      fieldName: leadField.name,
                      fieldType: leadField.type,
                      enumValue: selected.value,
                      enumIsNot: false,
                  };

        field.input.onChange(condition);

        // Now clear the enum field
        this.setState({field: null});
    };

    onCurrencyEnter = (field: {input: FieldProps['input']}) => () => {
        const leadField = this.state.field;
        if (!leadField) return;

        const condition = {
            fieldName: leadField.name,
            fieldType: leadField.type,
            currencyValue: this.state.currencyValue,
            currencyComparator: 'GREATER',
        } as const;

        field.input.onChange(condition);

        // Now clear the enum field
        this.setState({field: null});
    };
}

/*
 * When the user is selecting another field to add as a condition, we want to
 * prevent them from choosing a field which has alrady been configured.
 * Therefore, we can use this function to get back a list of fields that have
 * not yet been chosen.
 */
function getUnusedFields(
    allLeadFields: GoalFieldFragment[],
    currentFieldArray: FieldArrayProps['fields']
) {
    const selectedFieldNames = currentFieldArray
        .getAll()
        .filter(Boolean)
        // @ts-expect-error
        .map((condition) => condition.fieldName);

    return allLeadFields.filter((leadField) => !selectedFieldNames.includes(leadField.name));
}
